import { useEffect, useState } from "react";
import { http, formatINR, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { ProductMediaUploader, mediaSrc } from "@/components/ProductMediaUploader";
import { Users, Package, TrendingUp, ShieldCheck, DollarSign, ClipboardCheck, Check, X, Plus, FileText, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [pendingPracs, setPendingPracs] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [showProduct, setShowProduct] = useState(false);
  const [showBlog, setShowBlog] = useState(false);

  const load = async () => {
    const [s, pv, pp, av, o, bg, pr, cs] = await Promise.all([
      http.get("/admin/stats"),
      http.get("/vendors", { params: { status: "pending" } }),
      http.get("/products", { params: { status: "pending" } }),
      http.get("/vendors", { params: { status: "approved" } }),
      http.get("/orders"),
      http.get("/blogs"),
      http.get("/practitioners", { params: { status: "pending" } }),
      http.get("/admin/consultations"),
    ]);
    setStats(s.data);
    setPendingVendors(pv.data);
    setPendingProducts(pp.data);
    setApprovedVendors(av.data);
    setOrders(o.data);
    setBlogs(bg.data);
    setPendingPracs(pr.data);
    setConsultations(cs.data);
  };

  useEffect(() => { load(); }, []);

  const decideVendor = async (id, approved) => {
    try {
      await http.post(`/vendors/${id}/approval`, { approved });
      toast.success(approved ? "Vendor approved" : "Vendor rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const decideProduct = async (id, approved) => {
    try {
      await http.post(`/products/${id}/approval`, { approved });
      toast.success(approved ? "Product approved" : "Product rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const decidePrac = async (id, approved) => {
    try {
      await http.post(`/practitioners/${id}/approval`, { approved });
      toast.success(approved ? "Practitioner approved" : "Practitioner rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Amiroop Healthion LLP · Admin Control Panel</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-4xl font-bold text-[#1A201C] mt-1">Marketplace Command Center</h1>
        <div className="flex gap-2 mt-1">
          <button onClick={() => setShowProduct(true)} className="btn-forest text-sm inline-flex items-center gap-1" data-testid="admin-add-product-button">
            <Plus className="w-4 h-4" /> Add Healthion Product
          </button>
          <button onClick={() => setShowBlog(true)} className="btn-outline-forest text-sm inline-flex items-center gap-1" data-testid="admin-add-blog-button">
            <FileText className="w-4 h-4" /> New Blog
          </button>
        </div>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Verified Vendors" value={stats.total_vendors} icon={ShieldCheck} accent />
          <StatCard label="Pending KYC" value={stats.pending_vendors} icon={ClipboardCheck} />
          <StatCard label="Approved SKUs" value={stats.total_products} icon={Package} />
          <StatCard label="Total Customers" value={stats.total_customers} icon={Users} />
          <StatCard label="Gross Merchandise" value={formatINR(stats.gmv)} icon={TrendingUp} large />
          <StatCard label="Commission Earned" value={formatINR(stats.commission_earned)} icon={DollarSign} large />
          <StatCard label="Total Orders" value={stats.total_orders} icon={Package} large />
          <StatCard label="Pending Products" value={stats.pending_products} icon={ClipboardCheck} large />
        </div>
      )}

      <Tabs defaultValue="vendors" className="mt-10">
        <TabsList className="bg-white border border-[#E2DDD5]">
          <TabsTrigger value="vendors" data-testid="admin-tab-vendors">Vendor Moderation</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-tab-products">Product Moderation</TabsTrigger>
          <TabsTrigger value="practitioners" data-testid="admin-tab-practitioners">Practitioners</TabsTrigger>
          <TabsTrigger value="consultations" data-testid="admin-tab-consultations">Consultations</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="approved" data-testid="admin-tab-approved">Approved Vendors</TabsTrigger>
          <TabsTrigger value="blogs" data-testid="admin-tab-blogs">Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="mt-6">
          <div className="space-y-3">
            {pendingVendors.length === 0 ? (
              <p className="text-sm text-[#5A6B5D] text-center py-10">No pending vendor applications</p>
            ) : pendingVendors.map((v) => (
              <div key={v.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-5" data-testid={`pending-vendor-${v.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl font-semibold">{v.business_name}</div>
                    <div className="text-xs text-[#5A6B5D] mt-1">{v.description}</div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px] font-mono text-[#5A6B5D]">
                      <span>GSTIN: {v.gstin}</span>
                      <span>FSSAI: {v.fssai_license}</span>
                      <span>AYUSH: {v.ayush_license}</span>
                      <span>PAN: {v.pan}</span>
                    </div>
                    <VendorDocuments vendor={v} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => decideVendor(v.id, true)} className="btn-forest text-xs py-2 px-4 inline-flex items-center gap-1" data-testid={`approve-vendor-${v.id}`}>
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => decideVendor(v.id, false)} className="btn-outline-forest text-xs py-2 px-4 inline-flex items-center gap-1" data-testid={`reject-vendor-${v.id}`}>
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="grid gap-3">
            {pendingProducts.length === 0 ? (
              <p className="text-sm text-[#5A6B5D] text-center py-10">No products awaiting moderation</p>
            ) : pendingProducts.map((p) => (
              <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4 flex items-center gap-4" data-testid={`pending-product-${p.id}`}>
                <img src={mediaSrc(p.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-[#5A6B5D]">{p.vendor_name} · {p.category}</div>
                  <div className="text-xs text-[#5A6B5D] font-mono mt-1">Ingredients: {p.ingredients.join(", ")}</div>
                </div>
                <div className="text-sm font-bold text-[#1E3A2B]">{formatINR(p.price)}</div>
                <div className="flex gap-2">
                  <button onClick={() => decideProduct(p.id, true)} className="btn-forest text-xs py-2 px-3" data-testid={`approve-product-${p.id}`}>Approve</button>
                  <button onClick={() => decideProduct(p.id, false)} className="btn-outline-forest text-xs py-2 px-3" data-testid={`reject-product-${p.id}`}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="space-y-3">
            {orders.slice(0, 20).map((o) => (
              <div key={o.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4 flex justify-between items-center flex-wrap gap-2">
                <div>
                  <div className="text-xs font-mono text-[#5A6B5D]">#{o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="text-sm font-semibold">{o.user_email}</div>
                  <div className="text-xs text-[#5A6B5D]">{o.items.length} items · {o.payment_method?.toUpperCase()}</div>
                </div>
                <div className="text-sm font-bold text-[#1E3A2B]">{formatINR(o.total)}</div>
                <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full border bg-sky-50 border-sky-200 text-sky-800">
                  {o.status}
                </span>
              </div>
            ))}
            {orders.length === 0 && <p className="text-sm text-[#5A6B5D] text-center py-10">No orders yet</p>}
          </div>
        </TabsContent>

        <TabsContent value="practitioners" className="mt-6">
          <div className="space-y-3">
            {pendingPracs.length === 0 ? (
              <p className="text-sm text-[#5A6B5D] text-center py-10">No practitioner applications pending</p>
            ) : pendingPracs.map((p) => (
              <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-5" data-testid={`pending-prac-${p.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-xl font-semibold">{p.display_title} {p.full_name}</div>
                    <div className="text-xs text-[#5A6B5D] mt-1">{p.bio}</div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px] font-mono text-[#5A6B5D]">
                      <span>BAMS: {p.bams_reg_no}</span>
                      <span>Council #: {p.council_reg_no}</span>
                      <span>Council: {p.ncism_council}</span>
                      <span>City: {p.clinic_city}</span>
                      <span>Experience: {p.years_experience} yrs</span>
                      <span>Fee: {formatINR(p.consultation_fee)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => decidePrac(p.id, true)} className="btn-forest text-xs py-2 px-4 inline-flex items-center gap-1" data-testid={`approve-prac-${p.id}`}>
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => decidePrac(p.id, false)} className="btn-outline-forest text-xs py-2 px-4 inline-flex items-center gap-1" data-testid={`reject-prac-${p.id}`}>
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="consultations" className="mt-6">
          <div className="space-y-3">
            {consultations.length === 0 ? (
              <p className="text-sm text-[#5A6B5D] text-center py-10">No consultation bookings yet</p>
            ) : consultations.map((c) => (
              <div key={c.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4 flex justify-between items-center flex-wrap gap-3">
                <div>
                  <div className="text-xs font-mono text-[#5A6B5D]">#{c.id.slice(0, 8).toUpperCase()} · {c.mode.replace(/_/g, " ").toUpperCase()}</div>
                  <div className="text-sm font-semibold">{c.practitioner_name}</div>
                  <div className="text-xs text-[#5A6B5D]">Patient: {c.user_email} · {c.slot_date} @ {c.slot_time}</div>
                </div>
                <div className="text-right text-xs font-mono">
                  <div className="text-sm font-bold text-[#1E3A2B]">{formatINR(c.fee)}</div>
                  <div className="text-[#5A6B5D]">Commission {formatINR(c.platform_commission)}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {approvedVendors.map((v) => (
              <div key={v.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-5">
                <div className="font-serif text-lg font-semibold">{v.business_name}</div>
                <div className="text-xs text-[#5A6B5D]">{v.address}</div>
                <div className="mt-3 flex justify-between text-xs font-mono text-[#5A6B5D]">
                  <span>GST {v.gstin}</span>
                  <span className="text-[#1E3A2B] font-semibold">{formatINR(v.total_sales)}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="blogs" className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {blogs.length === 0 && <p className="text-sm text-[#5A6B5D] col-span-2 text-center py-10">No blogs yet. Publish your first Rasayana Journal entry.</p>}
            {blogs.map((b) => (
              <div key={b.id} className="bg-white border border-[#E2DDD5] rounded-2xl overflow-hidden" data-testid={`admin-blog-${b.slug}`}>
                <div className="aspect-[16/9] bg-[#E8F0EA]">
                  <img src={b.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <span className="badge-sage">{b.tag}</span>
                  <h3 className="mt-2 font-serif text-lg font-semibold">{b.title}</h3>
                  <p className="text-xs text-[#5A6B5D] mt-1 line-clamp-2">{b.excerpt}</p>
                  <div className="mt-2 flex justify-between items-center text-xs">
                    <span className="text-[#5A6B5D] font-mono">{b.author}</span>
                    <button
                      onClick={async () => {
                        try { await http.delete(`/blogs/${b.slug}`); toast.success("Blog deleted"); load(); }
                        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                      className="text-red-600 hover:underline"
                      data-testid={`delete-blog-${b.slug}`}
                    >Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {showProduct && <AdminProductModal onClose={() => { setShowProduct(false); load(); }} />}
      {showBlog && <AdminBlogModal onClose={() => { setShowBlog(false); load(); }} />}
    </div>
  );
}

function AdminProductModal({ onClose }) {
  const [f, setF] = useState({
    name: "", description: "", price: 0, original_price: 0, stock: 0,
    category: "herbal-supplements", dosha: "tridoshic",
    benefits: "", ingredients: "", images: [], video_url: null,
  });
  const submit = async (e) => {
    e.preventDefault();
    if (f.images.length === 0) return toast.error("Please upload at least one product photo");
    try {
      await http.post("/products", {
        ...f,
        price: parseFloat(f.price),
        original_price: parseFloat(f.original_price) || parseFloat(f.price),
        stock: parseInt(f.stock),
        benefits: f.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        ingredients: f.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Product added under Healthion Direct");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-product-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <span className="badge-sage">Healthion Direct</span>
            <h2 className="font-serif text-2xl font-bold mt-2">Add Marketplace Product</h2>
            <p className="text-xs text-[#5A6B5D]">Listed directly by Amiroop Healthion LLP (auto-approved).</p>
          </div>
          <input required placeholder="Product name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="admin-prod-name" />
          <textarea required placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm min-h-[80px]" />
          <div className="grid grid-cols-3 gap-3">
            <input required type="number" placeholder="Price ₹" value={f.price} onChange={(e) => set("price", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="admin-prod-price" />
            <input type="number" placeholder="MRP ₹" value={f.original_price} onChange={(e) => set("original_price", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
            <input required type="number" placeholder="Stock" value={f.stock} onChange={(e) => set("stock", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm">
              <option value="herbal-supplements">Herbal Supplements</option>
              <option value="personal-care">Personal Care</option>
              <option value="immunity-boosters">Immunity Boosters</option>
              <option value="organic-teas">Organic Teas</option>
              <option value="essential-oils">Essential Oils</option>
              <option value="wellness-foods">Wellness Foods</option>
              <option value="indian-masala">Indian Sabut & Pisa Masala</option>
            </select>
            <select value={f.dosha} onChange={(e) => set("dosha", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm">
              <option value="vata">Vata</option>
              <option value="pitta">Pitta</option>
              <option value="kapha">Kapha</option>
              <option value="tridoshic">Tridoshic</option>
            </select>
          </div>
          <input placeholder="Benefits (comma-separated)" value={f.benefits} onChange={(e) => set("benefits", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <input placeholder="Ingredients (comma-separated)" value={f.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <ProductMediaUploader images={f.images} videoUrl={f.video_url} onChange={({ images, videoUrl }) => setF({ ...f, images, video_url: videoUrl })} />
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#E2DDD5] text-sm">Cancel</button>
            <button className="btn-forest text-sm" data-testid="admin-submit-product">Publish to Marketplace</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminBlogModal({ onClose }) {
  const [f, setF] = useState({ title: "", excerpt: "", content: "", cover_image: "", tag: "Ayurveda", author: "Dr. Rupali Srivastava" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/blogs", f);
      toast.success("Blog published");
      onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-blog-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={submit} className="p-6 space-y-4">
          <h2 className="font-serif text-2xl font-bold">Publish to Rasayana Journal</h2>
          <input required placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="admin-blog-title" />
          <input required placeholder="Excerpt (2-3 lines)" value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <textarea required placeholder="Full content (use blank lines between paragraphs)" value={f.content} onChange={(e) => set("content", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm min-h-[160px]" data-testid="admin-blog-content" />
          <input required placeholder="Cover image URL" value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Tag" value={f.tag} onChange={(e) => set("tag", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
            <input placeholder="Author" value={f.author} onChange={(e) => set("author", e.target.value)} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" />
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#E2DDD5] text-sm">Cancel</button>
            <button className="btn-forest text-sm" data-testid="admin-submit-blog">Publish</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, large }) {
  return (
    <div className={`bg-white border border-[#E2DDD5] rounded-2xl p-4 ${accent ? "!bg-[#1E3A2B] !text-[#FAF8F5]" : ""}`}>
      <Icon className={`w-4 h-4 ${accent ? "text-[#D97706]" : "text-[#D97706]"}`} />
      <div className={`text-[10px] font-mono uppercase mt-2 ${accent ? "opacity-70" : "text-[#5A6B5D]"}`}>{label}</div>
      <div className={`font-serif font-bold mt-1 ${large ? "text-2xl" : "text-3xl"} ${accent ? "" : "text-[#1A201C]"}`}>{value}</div>
    </div>
  );
}

function VendorDocuments({ vendor }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get(`/vendors/${vendor.id}/documents`)
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [vendor.id]);

  const openDoc = async (docId) => {
    try {
      const resp = await fetch(`${API}/vendors/documents/${docId}`, { credentials: "include" });
      if (!resp.ok) throw new Error("Access failed");
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("Could not open document");
    }
  };

  if (loading) return <div className="mt-3 text-xs text-[#5A6B5D]">Loading KYC documents...</div>;
  if (docs.length === 0) return <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">⚠ No KYC documents uploaded</div>;
  return (
    <div className="mt-3">
      <div className="text-[10px] font-mono uppercase text-[#5A6B5D] mb-1.5">KYC Uploads</div>
      <div className="flex flex-wrap gap-2">
        {docs.map((d) => (
          <button
            key={d.id}
            onClick={() => openDoc(d.id)}
            className="inline-flex items-center gap-1.5 text-xs bg-[#E8F0EA] border border-[#1E3A2B]/20 text-[#1E3A2B] px-3 py-1.5 rounded-full hover:bg-[#1E3A2B] hover:text-white transition-colors"
            data-testid={`view-doc-${d.id}`}
          >
            <Download className="w-3 h-3" /> {d.doc_type.replace(/_/g, " ")}
          </button>
        ))}
      </div>
    </div>
  );
}
